import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function YogaVancouverGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "yoga-classes-groups-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Types of Yoga Available",
    "section-2": "Top Yoga Studios and Groups",
    "section-3": "Outdoor Yoga & Unique Experiences",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture.aspx",
      label: "City of Vancouver – Parks & Recreation",
    },
    {
      url: "https://matcollective.com/",
      label: "Mat Collective (Outdoor Yoga Nonprofit)",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Board",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <li> or <p> section. Coordinates go in `lat`/`lng`.
  const locations = [
    // Section-2: Studios
    {
      id: "yyoga-downtown-flow",
      lat: 49.28218,
      lng: -123.12398,
      popupText: "YYoga – Downtown Flow",
    },
    {
      id: "modo-yoga-kitsilano",
      lat: 49.26817,
      lng: -123.18596,
      popupText: "Modo Yoga Vancouver (Kitsilano)",
    },
    {
      id: "the-yoga-bar-yaletown",
      lat: 49.27668,
      lng: -123.12059,
      popupText: "The Yoga Bar – Yaletown",
    },
    {
      id: "just-add-water-yoga",
      lat: 49.29261,
      lng: -123.12869,
      popupText: "Just Add Water Yoga",
    },
    {
      id: "uphoria-yoga",
      lat: 49.26281,
      lng: -123.10343,
      popupText: "Üphoria Yoga – Mount Pleasant",
    },
    {
      id: "hot-yoga-101",
      lat: 49.26060,
      lng: -123.11672,
      popupText: "Hot Yoga 101 (Bikram Yoga Vancouver)",
    },
    {
      id: "jaybird-studio",
      lat: 49.27550,
      lng: -123.12456,
      popupText: "Jaybird Studio – Yaletown",
    },
    // Section-3: Outdoor Yoga Spots
    {
      id: "kitsilano-beach",
      lat: 49.27444,
      lng: -123.15349,
      popupText: "Beach Yoga at Kitsilano (Hadden Park)",
    },
    {
      id: "sunset-beach",
      lat: 49.27809,
      lng: -123.13790,
      popupText: "Sunset Beach Yoga",
    },
    {
      id: "stanley-park",
      lat: 49.30170,
      lng: -123.14170,
      popupText: "Stanley Park for Outdoor Sessions",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Yoga Classes and Groups in Vancouver - The Ultimate Guide</title>
        <meta
          name="description"
          content="Discover the best yoga studios, outdoor sessions, and community groups in Vancouver. Learn about styles, top studios, local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="yoga, Vancouver, studios, outdoor yoga, kits beach, mindfulness, wellness"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/yoga-classes-groups-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Yoga Classes and Groups in Vancouver - The Ultimate Guide"
        />
        <meta
          property="og:description"
          content="Discover top yoga options, from indoor studios to outdoor sessions, in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/yoga-classes-groups-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/yoga-classes-groups-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Yoga Classes and Groups in Vancouver - The Ultimate Guide"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's diverse yoga scene with studio highlights, outdoor yoga tips, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/yoga-classes-groups-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Yoga in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s love for yoga is no secret. Surrounded by ocean and
              mountains, the city provides a serene backdrop that enhances any
              yoga practice. Locals and visitors alike enjoy the variety of
              yoga styles, the sense of community, and the numerous indoor and
              outdoor class options available. Whether you’re aiming to reduce
              stress, increase flexibility, or simply discover a welcoming
              community, Vancouver’s yoga scene offers something for every level.
            </p>

            {/* SECTION: Section 1 - Types of Yoga */}
            <h2 id="section-1">Types of Yoga Available</h2>
            <p>
              Vancouver’s diversity extends to the yoga styles you can try. From
              calming Hatha sessions to vigorous Hot Yoga flows, each style
              caters to different preferences and fitness goals:
            </p>
            <ul>
              <li>
                <strong>Hatha Yoga:</strong> A classic, slower-paced approach
                focusing on fundamental poses and breathwork—great for beginners.
              </li>
              <li>
                <strong>Vinyasa Flow:</strong> An energetic practice that links
                each movement with breath for a dynamic, continuous sequence.
              </li>
              <li>
                <strong>Hot Yoga (Bikram, Hot Flow):</strong> Practiced in a
                heated room, it helps improve flexibility and detoxification.
              </li>
              <li>
                <strong>Yin &amp; Restorative Yoga:</strong> Slow and
                meditative, with longer pose holds to target deeper tissues—
                perfect for relaxation and stress relief.
              </li>
              <li>
                <strong>Aerial &amp; Acro Yoga:</strong> Unique practices
                that utilize hammocks or partner-based acrobatics, blending
                fun and fitness.
              </li>
              <li>
                <strong>Outdoor Yoga:</strong> Enjoy Vancouver’s scenic
                environment by taking your practice to beaches, parks, or
                forested trails when weather permits.
              </li>
            </ul>

            {/* SECTION: Section 2 - Top Yoga Studios and Groups */}
            <h2 id="section-2">Top Yoga Studios and Groups</h2>
            <p>
              When it comes to finding the right studio, Vancouver offers a range
              of boutique, community-oriented, and chain studios. Below are some
              of the city’s best-known spots, complete with approximate
              coordinates.
            </p>

            {/* YYoga Downtown Flow */}
            <p
              ref={(el) => setSpotRef("yyoga-downtown-flow", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>YYoga – Downtown Flow</strong>{" "}
              <Marker
                coordY={49.28218}
                coordX={-123.12398}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.28218, Longitude: -123.12398). Located on Burrard
              Street, this modern studio chain offers heated and non-heated
              classes, showers, and a relaxed lounge area. Expect diverse class
              styles like Flow, Power, and Yin.
            </p>

            {/* Modo Yoga Kitsilano */}
            <p
              ref={(el) => setSpotRef("modo-yoga-kitsilano", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Modo Yoga Vancouver (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.26817}
                coordX={-123.18596}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.26817, Longitude: -123.18596). This hot yoga
              specialist embraces an eco-friendly ethos. They offer donation-based
              Karma Classes, making yoga accessible to anyone.
            </p>

            {/* The Yoga Bar – Yaletown */}
            <p
              ref={(el) => setSpotRef("the-yoga-bar-yaletown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Yoga Bar – Yaletown</strong>{" "}
              <Marker
                coordY={49.27668}
                coordX={-123.12059}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.27668, Longitude: -123.12059). Known for
              infrared-heated sessions and aerial yoga, this chic studio also
              hosts community events and workshops.
            </p>

            {/* Just Add Water Yoga */}
            <p
              ref={(el) => setSpotRef("just-add-water-yoga", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Just Add Water Yoga</strong>{" "}
              <Marker
                coordY={49.29261}
                coordX={-123.12869}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.29261, Longitude: -123.12869). Nestled at the
              Westin Bayshore in Coal Harbour, they offer poolside or paddleboard
              yoga in warmer months and standard mat classes year-round.
            </p>

            {/* Üphoria Yoga */}
            <p
              ref={(el) => setSpotRef("uphoria-yoga", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Üphoria Yoga</strong>{" "}
              <Marker
                coordY={49.26281}
                coordX={-123.10343}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.26281, Longitude: -123.10343). A family-friendly
              studio offering adult classes, kids’ yoga, and even family yoga
              sessions. They also host yoga birthday parties for children.
            </p>

            {/* Hot Yoga 101 */}
            <p
              ref={(el) => setSpotRef("hot-yoga-101", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hot Yoga 101 (Bikram Yoga Vancouver)</strong>{" "}
              <Marker
                coordY={49.26060}
                coordX={-123.11672}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.26060, Longitude: -123.11672). Vancouver’s original
              hot yoga spot featuring 90-minute Bikram series, Inferno Hot
              Pilates, and other intense sweaty sessions.
            </p>

            {/* Jaybird Studio – Yaletown */}
            <p
              ref={(el) => setSpotRef("jaybird-studio", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Jaybird Studio – Yaletown</strong>{" "}
              <Marker
                coordY={49.27550}
                coordX={-123.12456}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.27550, Longitude: -123.12456). An infrared-heated
              studio offering music-driven flows, power yoga, and yoga-sculpt
              classes in a dimly lit, mirror-free environment.
            </p>

            {/* SECTION: Section 3 - Outdoor Yoga & Unique Experiences */}
            <h2 id="section-3">Outdoor Yoga &amp; Unique Experiences</h2>
            <p>
              Vancouver’s natural beauty makes it ideal for outdoor yoga sessions.
              Many local groups and studios host classes in parks, beaches, and
              community spaces during the warmer months.
            </p>

            {/* Kitsilano Beach */}
            <p
              ref={(el) => setSpotRef("kitsilano-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Beach Yoga at Kitsilano (Hadden Park)</strong>{" "}
              <Marker
                coordY={49.27444}
                coordX={-123.15349}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.27444, Longitude: -123.15349). Popular for
              community-led or donation-based classes through nonprofits like Mat
              Collective.
            </p>

            {/* Sunset Beach */}
            <p
              ref={(el) => setSpotRef("sunset-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Sunset Beach Yoga</strong>{" "}
              <Marker
                coordY={49.27809}
                coordX={-123.13790}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.27809, Longitude: -123.13790). A favorite spot for
              large evening classes during the summer. Enjoy views of English Bay
              while practicing under the open sky.
            </p>

            {/* Stanley Park */}
            <p
              ref={(el) => setSpotRef("stanley-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park for Outdoor Sessions</strong>{" "}
              <Marker
                coordY={49.30170}
                coordX={-123.14170}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – (Latitude: 49.30170, Longitude: -123.14170). Find informal
              gatherings or simply roll out a mat by the forest or along the
              shoreline. Some fitness groups host summer yoga near the Totem
              Poles or by Third Beach.
            </p>

            <p>
              Additionally, yoga festivals and retreats take place year-round. The
              Yoga &amp; Health Expo often occurs in early summer, featuring free
              classes, workshops, and community events. If you’re curious about
              stand-up paddleboard (SUP) yoga, English Bay and Jericho Beach are
              great places to give it a try.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations &amp; Best Practices</h2>
            <p>
              For individuals practicing in public parks, no permit is required.
              However, instructors who organize larger group classes do need a
              permit from the Vancouver Park Board. Always respect park etiquette:
              keep noise levels reasonable, avoid obstructing pathways, and
              practice “leave no trace” principles by cleaning up after yourself.
              If you’re new to hot yoga or have health concerns, consult a
              professional beforehand and stay hydrated.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Is there free or donation-based yoga in Vancouver?</h3>
              <p>
                Yes. Groups like Mat Collective and various community centers
                offer free or low-cost classes, especially during the summer
                months. Studios also host “Karma Classes,” where proceeds go to
                charity.
              </p>

              <h3>2. What is the typical cost for a drop-in class?</h3>
              <p>
                Expect to pay around $20–$25 for a drop-in at most private
                studios. Packages or membership options can reduce the
                per-class cost.
              </p>

              <h3>3. Do I need my own yoga mat?</h3>
              <p>
                It’s recommended, but many studios offer mat rentals or provide
                mats. For outdoor sessions, bringing your own mat is essential,
                and a towel is handy if the ground is damp.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re a seasoned yogi seeking a challenging flow or a
              beginner hoping to ease into a mindful practice, Vancouver’s yoga
              scene has you covered. Enjoy the benefits of indoor studios,
              connect with nature at outdoor sessions, and discover a supportive
              community along the way. Embrace the journey, and let your practice
              thrive in this beautiful coastal city.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}